import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Users, 
  TrendingUp, 
  DollarSign,
  Ticket,
  Trophy,
  Vote,
  Newspaper,
  BarChart3,
  Clock,
  Target,
  Star
} from 'lucide-react';
import axios from 'axios';
import { useDashboard } from '../../context/DashboardContext';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Widget configurations
const WIDGET_CONFIGS = {
  stats: {
    title: 'Quick Stats',
    icon: BarChart3,
    roles: ['super_admin', 'platform_admin', 'federation_admin', 'club_admin']
  },
  events: {
    title: 'Upcoming Events',
    icon: Calendar,
    roles: ['super_admin', 'platform_admin', 'federation_admin', 'club_admin', 'event_manager', 'fan']
  },
  revenue: {
    title: 'Revenue Overview',
    icon: DollarSign,
    roles: ['super_admin', 'platform_admin', 'federation_admin', 'club_admin']
  },
  membership: {
    title: 'Membership Stats',
    icon: Users,
    roles: ['super_admin', 'platform_admin', 'federation_admin', 'club_admin', 'academy_admin']
  },
  polls: {
    title: 'Active Polls',
    icon: Vote,
    roles: ['super_admin', 'platform_admin', 'federation_admin', 'club_admin', 'fan']
  },
  news: {
    title: 'Latest News',
    icon: Newspaper,
    roles: ['super_admin', 'platform_admin', 'federation_admin', 'club_admin']
  },
  tickets: {
    title: 'Ticket Sales',
    icon: Ticket,
    roles: ['super_admin', 'platform_admin', 'event_manager']
  },
  tournaments: {
    title: 'Tournament Progress',
    icon: Trophy,
    roles: ['super_admin', 'platform_admin', 'federation_admin', 'club_admin', 'event_manager']
  }
};

// Sample data for charts
const sampleRevenueData = [
  { name: 'Jan', value: 4000 },
  { name: 'Feb', value: 3000 },
  { name: 'Mar', value: 5000 },
  { name: 'Apr', value: 4500 },
  { name: 'May', value: 6000 },
  { name: 'Jun', value: 5500 }
];

const membershipData = [
  { name: 'Bronze', value: 400, color: '#CD7F32' },
  { name: 'Silver', value: 300, color: '#C0C0C0' },
  { name: 'Gold', value: 200, color: '#FFD700' },
  { name: 'Platinum', value: 100, color: '#E5E4E2' }
];

// Individual Widget Components
const UpcomingEventsWidget = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/events?limit=3`)
      .then(res => setEvents(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {events.length === 0 ? (
        <p className="text-white/40 text-sm text-center py-4">No upcoming events</p>
      ) : (
        events.map(event => (
          <div key={event.id} className="flex items-center gap-3 p-2 rounded-lg bg-accent/30">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Calendar size={16} className="text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-body truncate">{event.name}</p>
              <p className="text-white/40 text-xs">{new Date(event.start_date).toLocaleDateString()}</p>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const RevenueWidget = () => {
  return (
    <div className="h-40">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={sampleRevenueData}>
          <defs>
            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis dataKey="name" stroke="#666" fontSize={10} />
          <YAxis stroke="#666" fontSize={10} />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#1a1a1a', 
              border: '1px solid #333',
              borderRadius: '8px'
            }}
          />
          <Area 
            type="monotone" 
            dataKey="value" 
            stroke="#10B981" 
            fillOpacity={1} 
            fill="url(#colorRevenue)" 
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const MembershipWidget = () => {
  return (
    <div className="flex items-center gap-4">
      <div className="w-32 h-32">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={membershipData}
              cx="50%"
              cy="50%"
              innerRadius={25}
              outerRadius={45}
              paddingAngle={2}
              dataKey="value"
            >
              {membershipData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#1a1a1a', 
                border: '1px solid #333',
                borderRadius: '8px'
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-2">
        {membershipData.map(tier => (
          <div key={tier.name} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tier.color }} />
            <span className="text-white/70 text-xs">{tier.name}</span>
            <span className="text-white text-xs font-bold">{tier.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ActivePollsWidget = () => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/polls?limit=2`)
      .then(res => setPolls(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {polls.length === 0 ? (
        <p className="text-white/40 text-sm text-center py-4">No active polls</p>
      ) : (
        polls.slice(0, 2).map(poll => (
          <div key={poll.id} className="p-3 rounded-lg bg-accent/30">
            <p className="text-white text-sm font-body mb-2 line-clamp-2">{poll.question}</p>
            <div className="flex items-center justify-between text-xs">
              <span className="text-primary">{poll.total_votes} votes</span>
              <span className={`px-2 py-0.5 rounded-full ${poll.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                {poll.is_active ? 'Active' : 'Closed'}
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const QuickStatsWidget = () => {
  const [stats, setStats] = useState({});
  
  useEffect(() => {
    axios.get(`${API}/stats/overview`)
      .then(res => setStats(res.data))
      .catch(err => console.error(err));
  }, []);

  const quickStats = [
    { label: 'Events', value: stats.total_events || 0, icon: Calendar, color: 'text-blue-400' },
    { label: 'Members', value: stats.total_memberships || 0, icon: Users, color: 'text-green-400' },
    { label: 'Tickets', value: stats.total_tickets || 0, icon: Ticket, color: 'text-yellow-400' },
    { label: 'Orgs', value: stats.total_organizations || 0, icon: Target, color: 'text-purple-400' }
  ];

  return (
    <div className="grid grid-cols-2 gap-3">
      {quickStats.map(stat => (
        <div key={stat.label} className="flex items-center gap-2 p-2 rounded-lg bg-accent/30">
          <stat.icon size={16} className={stat.color} />
          <div>
            <p className="text-white font-bold text-sm">{stat.value}</p>
            <p className="text-white/40 text-xs">{stat.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// Main Dashboard Widgets Component
const DashboardWidgets = () => {
  const { userRole, widgetPreferences } = useDashboard();

  // Get widgets available for current role
  const availableWidgets = Object.entries(WIDGET_CONFIGS)
    .filter(([key, config]) => config.roles.includes(userRole))
    .slice(0, 4); // Limit to 4 widgets

  const renderWidgetContent = (widgetKey) => {
    switch (widgetKey) {
      case 'events':
        return <UpcomingEventsWidget />;
      case 'revenue':
        return <RevenueWidget />;
      case 'membership':
        return <MembershipWidget />;
      case 'polls':
        return <ActivePollsWidget />;
      case 'stats':
        return <QuickStatsWidget />;
      default:
        return <p className="text-white/40 text-sm">Widget content coming soon</p>;
    }
  };

  if (availableWidgets.length === 0) return null;

  return (
    <div className="grid md:grid-cols-2 gap-4 md:gap-6">
      {availableWidgets.map(([key, config], index) => {
        const IconComponent = config.icon;
        return (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-card border border-border/40 rounded-xl p-4 md:p-5"
            data-testid={`widget-${key}`}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <IconComponent size={16} className="text-primary" />
              </div>
              <h3 className="font-heading text-sm font-bold text-white">{config.title}</h3>
            </div>
            {renderWidgetContent(key)}
          </motion.div>
        );
      })}
    </div>
  );
};

export default DashboardWidgets;
